import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "./motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useT } from "@/i18n";

export const SectionHeading = ({ eyebrow, title, lines, intro, light = false, className = "" }) => (
  <div className={className}>
    {eyebrow && <Reveal><p className={light ? "eyebrow-light" : "eyebrow-dark"}>{eyebrow}</p></Reveal>}
    <h2 className={`h-display mt-8 text-[2.9rem] sm:text-6xl lg:text-[5.75rem] ${light ? "text-ivory" : "text-navy"}`}>
      <MaskedLines lines={lines || [title]} inView delay={0} />
    </h2>
    {intro && <Reveal delay={0.15}><p className={`lede mt-10 max-w-2xl ${light ? "text-ivory/80" : "text-charcoal-soft"}`}>{intro}</p></Reveal>}
  </div>
);

export const CTABanner = ({ title = "Your next chapter deserves a considered beginning.", text = "Arrange a private, confidential consultation with a senior adviser — in person in Ahmedabad, by video or by telephone." }) => {
  const { t } = useT();
  return (
    <section className="grain relative overflow-hidden bg-navy py-36 text-ivory md:py-52 lg:py-64" data-testid="cta-banner">
      <img src="/img/jet-2.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
      <div className="container-lux relative z-10">
        <p className="eyebrow-light">Private Client Desk</p>
        <h2 className="h-display mt-10 max-w-5xl text-5xl sm:text-7xl lg:text-[6.5rem]"><MaskedLines lines={[title]} inView delay={0} /></h2>
        <Reveal delay={0.2}><p className="lede mt-10 max-w-xl text-ivory/80">{text}</p></Reveal>
        <Reveal delay={0.3} className="mt-14 flex flex-col gap-4 sm:flex-row">
          <Link to="/contact" className="btn-gold" data-testid="cta-banner-book-btn"><span>{t("cta.book")}</span><ArrowRight className="h-4 w-4" /></Link>
          <Link to="/eligibility" className="btn-outline-light" data-testid="cta-banner-eligibility-btn">{t("cta.eligibility")}</Link>
        </Reveal>
      </div>
    </section>
  );
};

export const FAQList = ({ items, prefix = "faq" }) => (
  <Accordion type="single" collapsible className="border-t border-navy/15">
    {items.map((f, i) => (
      <AccordionItem key={i} value={`${prefix}-${i}`} className="border-navy/15">
        <AccordionTrigger data-testid={`${prefix}-item-${i}`} className="py-9 text-left font-serif text-2xl font-light text-navy hover:no-underline md:text-[2rem] md:leading-tight [&>svg]:text-gold-deep">{f.q}</AccordionTrigger>
        <AccordionContent className="max-w-3xl pb-9 text-[16px] font-light leading-[1.85] text-charcoal-soft">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const Marquee = ({ items, className = "" }) => (
  <div className={`relative overflow-hidden ${className}`} aria-hidden="true">
    <div className="flex w-max animate-marquee">
      {[...items, ...items].map((w, i) => (
        <span key={i} className="flex items-center whitespace-nowrap font-serif text-6xl font-light italic md:text-8xl">
          <span className={i % 2 ? "text-outline" : "text-navy"}>{w}</span>
          <span className="mx-10 text-3xl not-italic text-gold md:mx-14">✦</span>
        </span>
      ))}
    </div>
  </div>
);
